// Settings > Advanced > Linux machines: preferences for the /linux page (src/client/linux/) plus what it stores in this
// browser. Machines themselves are managed on the Linux page; these are the defaults and the
// switches. Storage numbers come from shared/linux-store.js (snapshots + uploads counted exactly,
// CheerpX disks by count - the runtime doesn't expose their size).
import { toggle, select, range, textInput, button } from "../ui.js";
import { get, set, subscribe } from "../../shared/settings-store.js";
import { usage, deleteAllLinuxData, CHEERPX_KEY } from "../../shared/linux-store.js";
import { readJSON, remove, subscribe as onData } from "../../shared/store.js";
import { LINUX_RELAY_OK_KEY, unconfirmedRelayHost } from "../../shared/settings-schema.js";

const fmt = (n) => { const mb = (n || 0) / 1048576; return !mb ? "0 MB" : mb >= 1024 ? (mb / 1024).toFixed(2) + " GB" : mb.toFixed(mb >= 10 ? 0 : 1) + " MB"; };

// Live readout of what Linux keeps here, with the origin-wide estimate as context.
function usageControl() {
	const out = document.createElement("div");
	out.className = "rv-row-desc";
	out.setAttribute("aria-live", "polite");
	const render = async () => {
		try {
			const u = await usage();
			let line = fmt(u.snapshots + u.images) + " (" + u.snapshotCount + " saved, " + u.imageCount + " uploaded" + (u.cheerpxDisks ? ", " + Math.ceil(u.cheerpxDisks / 2) + " CheerpX" : "") + ")";
			if (u.estimate) line += ". Browser: " + fmt(u.estimate.usage) + " of " + fmt(u.estimate.quota);
			out.textContent = line;
		} catch (e) { out.textContent = "Storage info isn't available in this browser."; }
	};
	render();
	return { el: out, render };
}

// The CheerpX licence is accepted in a blocking notice on the Linux page the first time a CheerpX
// machine starts; this shows the record and lets you withdraw it (the notice then shows again).
function licenceControl() {
	const wrap = document.createElement("div");
	const render = () => {
		const r = readJSON(CHEERPX_KEY, null);
		wrap.textContent = "";
		if (r && r.accepted) {
			const when = document.createElement("span");
			when.className = "rv-row-desc";
			when.textContent = "Accepted " + new Date(r.acceptedAt).toLocaleDateString() + " ";
			wrap.append(when, button("Withdraw", () => { remove(CHEERPX_KEY); render(); }));
		} else {
			const s = document.createElement("span");
			s.className = "rv-row-desc";
			s.textContent = "Not accepted yet (asked when you first start a CheerpX machine)";
			wrap.append(s);
		}
	};
	render();
	onData(CHEERPX_KEY, render);
	return wrap;
}

// The custom relay box. Another site's relay sees every connection the Linux guests make, so it is
// used only after a yes on this device (stored under LINUX_RELAY_OK_KEY, outside revault:*, so no
// savefile or backup carries it). A saved value without that yes (one saved before this check
// existed, or one from a backup) is shown with a button to confirm it, since typing the same text
// again does not fire "change".
const relayOk = () => { try { return localStorage.getItem(LINUX_RELAY_OK_KEY) || ""; } catch { return ""; } };
function setRelayOk(v) { try { if (v) localStorage.setItem(LINUX_RELAY_OK_KEY, v); else localStorage.removeItem(LINUX_RELAY_OK_KEY); } catch (e) { void e; } }
function relayControl() {
	const wrap = document.createElement("div");
	const wisp = textInput("linuxWispUrl", "wss://… (blank uses Re:Vault's own)");
	const note = document.createElement("div");
	note.className = "rv-row-desc";
	const confirmBtn = button("Use this relay", () => ask(String(get("linuxWispUrl") || "").trim()));
	const paint = () => {
		const host = unconfirmedRelayHost(get("linuxWispUrl"), relayOk(), location.host);
		note.textContent = host ? "Not in use: " + host + " has not been confirmed on this device, so Re:Vault's relay is used." : "";
		note.style.display = confirmBtn.style.display = host ? "" : "none";
	};
	function ask(v) {
		let host = "";
		try { host = v ? new URL(v).host : ""; } catch { host = ""; }
		if (!host || host === location.host) { setRelayOk(""); paint(); return; }
		if (confirm("Send Linux network traffic through " + host + "? That relay can see every connection your Linux machines make. Only use one you trust.")) setRelayOk(v);
		else { setRelayOk(""); wisp.value = ""; set("linuxWispUrl", ""); }
		paint();
	}
	wisp.addEventListener("change", () => {
		const v = wisp.value.trim();
		if (v && !/^wss?:\/\/[^\s/#]+[^\s#]*$/i.test(v)) { alert("Use a ws:// or wss:// address, or leave it blank."); wisp.value = ""; set("linuxWispUrl", ""); setRelayOk(""); paint(); return; }
		ask(v);
	});
	subscribe(paint);
	wrap.append(wisp, note, confirmBtn);
	paint();
	return wrap;
}

export function linuxControls() {
	const u = usageControl();
	subscribe(() => u.render());
	return {
		linuxEngine: () => select("linuxEngine", [["v86", "v86 (Alpine Linux)"], ["cheerpx", "CheerpX (Debian or a desktop)"]]),
		linuxCheerpx: () => toggle("linuxCheerpx"),
		cheerpxLicence: licenceControl,
		linuxMemory: () => range("linuxMemory", 128, 1024, 128, (v) => v + " MB"),
		linuxNetwork: () => toggle("linuxNetwork"),
		linuxWispUrl: relayControl,
		linuxStorage: () => u.el,
		linuxDelete: () => button("Delete Linux data", async () => {
			if (!confirm("Delete all Linux data? Every machine, saved state, uploaded image and CheerpX disk in this browser is erased. This can't be undone.")) return;
			const r = await deleteAllLinuxData();
			u.render();
			alert(r.failed.length ? "Most Linux data was deleted, but " + r.failed.length + " disk(s) are still open in a running Linux tab. Stop or close it, then try again." : "All Linux data was deleted.");
		}, "rv-btn-danger"),
	};
}
